import React, { useState, useEffect } from "react";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { ApiResponse } from "../types/common";
import { settingsRelatedApi } from "../utils/api";

interface AthleteNotificationData {
  newMessages: boolean;
  critiqueReceived: boolean;
  packageSubscriptionCreated: boolean;
  promotionalMessages: boolean;
}

export function AthleteNotificationSettings() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<AthleteNotificationData>({
    newMessages: true,
    critiqueReceived: true,
    packageSubscriptionCreated: false, // Unchecked as shown in the image
    promotionalMessages: true
  });

  // Simulate API call to fetch notification settings
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response: ApiResponse = await settingsRelatedApi.getAthleteSettings();
      
        if (response.success) {
          setSettings(response.data || []);
        } else {
          throw new Error(response.message || 'Failed to load user stats');
        }
        
        // Settings are already set in state with default values matching the image
      } catch (error) {
        console.error("Failed to fetch athlete notification settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call to save settings
      // const response = await fetch("host/api/v1/athlete-notification-settings", {
      //   method: 'PUT',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${localStorage.getItem("authToken")}`
      //   },
      //   body: JSON.stringify(settings)
      // });

      console.log("Saving athlete notification settings:", settings);
      
      // Mock success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // You could show a success toast here
    } catch (error) {
      console.error("Failed to save athlete notification settings:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
        </Button>
        <div>
          <h1>Edit</h1>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="space-y-6">
        {/* New Messages */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="newMessages"
                checked={settings.newMessages}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, newMessages: !!checked }))
                }
              />
              <Label htmlFor="newMessages">
                New Messages
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Critique Received */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="critiqueReceived"
                checked={settings.critiqueReceived}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, critiqueReceived: !!checked }))
                }
              />
              <Label htmlFor="critiqueReceived">
                Critique Received
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Package Subscription Created */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="packageSubscriptionCreated"
                checked={settings.packageSubscriptionCreated}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, packageSubscriptionCreated: !!checked }))
                }
              />
              <Label htmlFor="packageSubscriptionCreated">
                Package Subscription Created
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Promotional Messages */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="promotionalMessages"
                checked={settings.promotionalMessages}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, promotionalMessages: !!checked }))
                }
              />
              <Label htmlFor="promotionalMessages">
                Promotional Messages
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-center pt-8">
          <Button 
            onClick={handleSave}
            disabled={isSaving}
            className="bg-blue-500 hover:bg-blue-600 text-white min-w-[120px] px-8"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}